import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  date,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Legacy simple tasks table - kept for backward compatibility during migration
// Will be removed after data migration is complete
export const legacyTasks = pgTable("legacy_tasks", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  priority: text("priority").notNull().default("medium"),
  status: text("status").notNull().default("pending"),
  dueDate: timestamp("due_date"),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  taskScope: text("task_scope").default("personal"),
  teamId: integer("team_id"),
  assignedTo: varchar("assigned_to"),
  createdBy: varchar("created_by"),
  isAiGenerated: boolean("is_ai_generated").default(false),
  aiConfidenceScore: decimal("ai_confidence_score", { precision: 3, scale: 2 }),
  source: text("source").default("manual"),
  relatedObjectType: text("related_object_type"),
  relatedObjectId: integer("related_object_id"),
  suggestedAt: timestamp("suggested_at"),
  approvedAt: timestamp("approved_at"),
  approvedBy: varchar("approved_by"),
});

export const appointments = pgTable("appointments", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"), // references clients.id
  prospectId: integer("prospect_id"), // references prospects.id
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time").notNull(),
  status: text("status").notNull().default("scheduled"), // scheduled, completed, cancelled
  type: text("type").notNull(), // meeting, call, review, consultation
  location: text("location"),
  reminderMinutes: integer("reminder_minutes"), // null = no reminder, otherwise minutes before meeting (5, 10, 15, 30, 60)
  reminderSent: timestamp("reminder_sent"), // when the reminder was shown (to prevent duplicates)
  invitedTeamMembers: jsonb("invited_team_members").$type<string[]>(), // array of team member IDs
  manualInvites: text("manual_invites"), // comma-separated email addresses
});

// Main Task System - renamed from tickets to tasks for clarity
export const tasks = pgTable("tasks", {
  id: serial("id").primaryKey(),
  taskNumber: text("task_number").notNull().unique(), // auto-generated unique identifier (TSK-format)
  title: text("title").notNull(),
  description: text("description").notNull(),
  priority: text("priority").notNull().default("medium"), // low, medium, high, urgent
  status: text("status").notNull().default("open"), // open, in_progress, pending_info, resolved, closed
  category: text("category").notNull(), // benefits_enrollment, plan_changes, claims, billing, general
  subcategory: text("subcategory"), // specific subcategory based on category

  // Requester information
  requesterId: varchar("requester_id"), // references users.id
  requesterEmail: text("requester_email").notNull(),
  requesterName: text("requester_name").notNull(),
  requesterPhone: text("requester_phone"),
  requesterType: text("requester_type").notNull(), // client_admin, employee, dependent

  // Client/Employee/Dependent association
  clientId: integer("client_id"), // references clients.id
  employeeId: integer("employee_id"), // references employees.id
  dependentId: integer("dependent_id"), // references dependents.id

  // Assignment and handling
  assignedTo: varchar("assigned_to"), // references users.id - unified assignment field
  assignedAt: timestamp("assigned_at"), // when task was assigned
  agencyId: varchar("agency_id"), // references agencies.id
  teamId: integer("team_id"), // references agency_teams.id - for team-based visibility
  createdBy: varchar("created_by"), // references users.id - task creator

  // Resolution details
  resolution: text("resolution"),
  resolutionNotes: text("resolution_notes"),
  estimatedResolutionDate: timestamp("estimated_resolution_date"),
  actualResolutionDate: timestamp("actual_resolution_date"),
  dueDate: timestamp("due_date"), // Convenient alias for simple task management

  // Tracking and metadata
  lastActivityDate: timestamp("last_activity_date").defaultNow(),
  tags: text("tags").array().default([]),
  attachments: jsonb("attachments").default([]),
  customFields: jsonb("custom_fields").default({}),

  // Task scope and source tracking
  taskScope: text("task_scope").default("team"), // 'team' or 'personal' - team tasks are visible to team members
  source: text("source").default("manual"), // 'manual', 'system', 'ai_suggested', 'ai_auto'

  // Enrollment progress tracking (for enrollment-related tasks)
  // Tracks where employee is in the enrollment workflow: not_started, viewed, in_progress, submitted, under_review, confirmed, declined
  enrollmentProgress: text("enrollment_progress"),
  enrollmentProgressUpdatedAt: timestamp("enrollment_progress_updated_at"),

  // Workflow automation fields
  dedupeKey: text("dedupe_key"), // Cron idempotency key (e.g. workflow:event_type:clientId:year)
  parentTaskId: integer("parent_task_id"), // For parent/child task relationships (e.g. termination -> COBRA)

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("tasks_dedupe_idx").on(table.dedupeKey),
]);

export const taskComments = pgTable("task_comments", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(), // references tasks.id
  authorId: varchar("author_id"), // references users.id
  authorName: text("author_name").notNull(),
  authorEmail: text("author_email").notNull(),
  authorType: text("author_type").notNull(), // broker, client_admin, employee, system
  content: text("content").notNull(),
  isInternal: boolean("is_internal").default(false), // internal broker notes vs client-visible
  attachments: jsonb("attachments").default([]),
  createdAt: timestamp("created_at").defaultNow(),
});

export const taskHistory = pgTable("task_history", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(), // references tasks.id
  changedById: varchar("changed_by_id"), // references users.id
  changedByName: text("changed_by_name").notNull(),
  changeType: text("change_type").notNull(), // status_change, assignment, priority_change, etc.
  fieldName: text("field_name"),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  description: text("description").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Task Checklist Items - individual checklist steps within a workflow task
export const taskChecklistItems = pgTable("task_checklist_items", {
  id: serial("id").primaryKey(),
  taskId: integer("task_id").notNull(),
  label: text("label").notNull(),
  isCompleted: boolean("is_completed").notNull().default(false),
  completedAt: timestamp("completed_at"),
  completedById: varchar("completed_by_id"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("task_checklist_task_idx").on(table.taskId),
]);

// Event to Task Mapping table (for system/AI-generated tasks)
export const eventToTaskMappings = pgTable("event_to_task_mappings", {
  id: serial("id").primaryKey(),
  eventType: text("event_type").notNull().unique(), // 'enrollment_submitted', 'employee_terminated', 'quote_accepted', etc.
  defaultTaskTitle: text("default_task_title").notNull(),
  defaultTaskDescription: text("default_task_description"),
  defaultPriority: text("default_priority").notNull().default("medium"), // low, medium, high
  defaultScope: text("default_scope").notNull().default("team"), // team, personal
  allowAi: boolean("allow_ai").default(true), // whether AI can enhance/modify this task
  isActive: boolean("is_active").default(true), // enable/disable this mapping
  config: jsonb("config").default({}), // additional configuration (assignee logic, etc.)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// AI Task Suggestions table (for suggested but not yet approved tasks)
export const taskSuggestions = pgTable("task_suggestions", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  title: text("title").notNull(),
  description: text("description"),
  priority: text("priority").notNull().default("medium"),
  suggestedAssignee: varchar("suggested_assignee"), // references users.id
  confidenceScore: decimal("confidence_score", { precision: 3, scale: 2 }), // 0.00 - 1.00
  relatedObjectType: text("related_object_type"),
  relatedObjectId: integer("related_object_id"),
  eventType: text("event_type"), // references eventToTaskMappings.eventType
  status: text("status").notNull().default("pending"), // pending, approved, dismissed, expired
  approvedAt: timestamp("approved_at"),
  approvedBy: varchar("approved_by"),
  dismissedAt: timestamp("dismissed_at"),
  dismissedBy: varchar("dismissed_by"),
  expiresAt: timestamp("expires_at"), // suggestions expire after a period
  createdAt: timestamp("created_at").defaultNow(),
});

// Insert schemas
export const insertTaskSchema = createInsertSchema(tasks);
export const insertTaskCommentSchema = createInsertSchema(taskComments);
export const insertTaskHistorySchema = createInsertSchema(taskHistory);
export const insertTaskChecklistItemSchema = createInsertSchema(taskChecklistItems).omit({
  id: true,
  createdAt: true,
});

export const insertEventToTaskMappingSchema = createInsertSchema(eventToTaskMappings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTaskSuggestionSchema = createInsertSchema(taskSuggestions).omit({
  id: true,
  createdAt: true,
  approvedAt: true,
  dismissedAt: true,
});

export const insertAppointmentSchema = createInsertSchema(appointments).omit({
  id: true,
});

// Types
export type Task = typeof tasks.$inferSelect;
export type InsertTask = typeof tasks.$inferInsert;
export type TaskComment = typeof taskComments.$inferSelect;
export type InsertTaskComment = typeof taskComments.$inferInsert;
export type TaskHistory = typeof taskHistory.$inferSelect;
export type InsertTaskHistory = typeof taskHistory.$inferInsert;
export type TaskChecklistItem = typeof taskChecklistItems.$inferSelect;
export type InsertTaskChecklistItem = z.infer<typeof insertTaskChecklistItemSchema>;
export type EventToTaskMapping = typeof eventToTaskMappings.$inferSelect;
export type InsertEventToTaskMapping = z.infer<typeof insertEventToTaskMappingSchema>;
export type TaskSuggestion = typeof taskSuggestions.$inferSelect;
export type InsertTaskSuggestion = z.infer<typeof insertTaskSuggestionSchema>;
export type Appointment = typeof appointments.$inferSelect;
export type InsertAppointment = z.infer<typeof insertAppointmentSchema>;

// Legacy type aliases for backward compatibility during migration
export type Ticket = Task;
export type InsertTicket = InsertTask;
export type TicketComment = TaskComment;
export type InsertTicketComment = InsertTaskComment;
export type TicketHistory = TaskHistory;
export type InsertTicketHistory = InsertTaskHistory;

// Legacy schema aliases for backward compatibility
export const tickets = tasks;
export const ticketComments = taskComments;
export const ticketHistory = taskHistory;
export const insertTicketSchema = insertTaskSchema;
export const insertTicketCommentSchema = insertTaskCommentSchema;
export const insertTicketHistorySchema = insertTaskHistorySchema;

// Task scope and source enums (for type safety)
export const TaskScope = {
  TEAM: "team",
  PERSONAL: "personal",
} as const;

export const TaskSource = {
  MANUAL: "manual",
  SYSTEM: "system",
  AI_SUGGESTED: "ai_suggested",
  AI_AUTO: "ai_auto",
} as const;

export const TaskSuggestionStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  DISMISSED: "dismissed",
  EXPIRED: "expired",
} as const;

export const RelatedObjectType = {
  ENROLLMENT: "enrollment",
  EMPLOYEE: "employee",
  QUOTE: "quote",
  CLIENT: "client",
  PROSPECT: "prospect",
} as const;

// Bug Reports table - for bug reporting by all users
export const bugReports = pgTable("bug_reports", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  severity: text("severity").notNull().default("medium"), // low, medium, high, critical
  pageUrl: text("page_url"), // auto-captured current URL for context
  reportedBy: varchar("reported_by"), // user ID
  reporterEmail: text("reporter_email"),
  reporterName: text("reporter_name"),
  context: jsonb("context"), // auto-captured console logs, network requests, browser info
  status: text("status").notNull().default("open"), // open, resolved, closed
  assigneeId: varchar("assignee_id"), // superadmin user assigned to this bug
  assigneeEmail: text("assignee_email"),
  attachments: jsonb("attachments").default([]), // array of { s3Key, s3Url, filename, contentType, uploadedAt }
  adminNotes: text("admin_notes"), // internal notes from superadmins
  // Linear integration fields
  linearIssueId: text("linear_issue_id"), // Linear UUID
  linearIssueUrl: text("linear_issue_url"), // clickable link to Linear issue
  linearIssueIdentifier: text("linear_issue_identifier"), // e.g. "ADM-42"
  environment: text("environment").notNull().default("production"), // "staging" | "production"
  linearSyncError: text("linear_sync_error"), // last sync failure message
  linearSyncedAt: timestamp("linear_synced_at"), // last successful sync timestamp
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("bug_reports_linear_issue_idx").on(table.linearIssueId),
]);

export const insertBugReportSchema = createInsertSchema(bugReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  linearIssueId: true,
  linearIssueUrl: true,
  linearIssueIdentifier: true,
  linearSyncError: true,
  linearSyncedAt: true,
});

export type BugReport = typeof bugReports.$inferSelect;
export type InsertBugReport = z.infer<typeof insertBugReportSchema>;
